import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {
    AdminLoginUser,
    UserPreferredGlobalCategoryViewModel,
    UserPreferredProductViewModel,
    UserProfileViewModel, UserSport
} from '../viewModels/userProfile.viewModel';
import {Constants} from '../config/constants.config';
import {TrainingDurationViewModel} from '../viewModels/trainingDuration.viewModel';
import {DailyChangesActivitiesViewModel, TrainingActivityViewModel} from '../viewModels/trainingActivity.viewModel';
import {TrainingTargetViewModel} from '../viewModels/trainingTarget.viewModel';
import {
    ActivityViewModel,
    FatHoldViewModel,
    GlobalProfilesViewModel,
    HowLongFoodPrepareViewModel,
    WhenPrepareFoodViewModel
} from '../viewModels/activity.viewModel';
import {GenderViewModel} from '../viewModels/gender.viewModel';
import {BodyTypeViewModel} from '../viewModels/bodyType.viewModel';
import {AuthEventsEmitter} from '../events/auth.events';
import {DailyMealViewModel} from '../viewModels/dailyMeal.viewModel';
import {CateringUserViewModel, DietViewModel} from '../viewModels/diet.viewModel';
import {SubscriptionViewModel} from '../viewModels/subscription.viewModel';
import {formatDate} from '../utilities/date.utilities';
import {UserWaterViewModel} from '../viewModels/userWater.viewModel';
import {AddMlToUserWaterDTO} from '../dto/addMlToUserWater.dto';
import {
    IsCurrentUserReferralViewModel,
    ReferralsStatisticViewModel,
    ReferralViewModel,
    UpdatedReferralNameViewModel
} from '../viewModels/referral.viewModel';
import {TrainingViewModel} from '../viewModels/training.viewModel';
import {Languages} from '../enums/languages';

@Injectable()
export class UsersService {
    userProfile: UserProfileViewModel;

    constructor(
        private readonly httpClient: HttpClient,
        private readonly constants: Constants,
        private readonly authEventsEmitter: AuthEventsEmitter
    ) {
        this.authEventsEmitter.userProfileChanged$
            .subscribe(userProfile => {
                this.userProfile = userProfile;

                console.log('gtag id');

                // @ts-ignore
                window.dataLayer = window.dataLayer || [];
                // @ts-ignore
                window.dataLayer.push({
                    'userId': userProfile.id + ''
                });
            });
    }

    getUserProfile(doNotEmit = false): Observable<UserProfileViewModel> {
        return this.httpClient.get<UserProfileViewModel>(`${this.constants.apiURL()}/user/profile/`)
            .pipe(
                map(userProfile => {
                    if (doNotEmit === false) {
                        this.authEventsEmitter.broadcastUserProfileChanged(userProfile);
                    }

                    return userProfile;
                })
            );
    }

    getMasterUserProfile(): Observable<UserProfileViewModel> {
        return this.httpClient.get<UserProfileViewModel>(`${this.constants.apiURL()}/master/user/profile/`)
            .pipe(
                map(userProfile => {
                    return userProfile;
                })
            );
    }

    getUserSubscription(): Observable<SubscriptionViewModel> {
        return this.httpClient.get<SubscriptionViewModel>(`${this.constants.apiURL()}/subscription/`);
    }

    deleteUserSubscription(): Observable<void> {
        return this.httpClient.delete<void>(`${this.constants.apiURL()}/subscription/`);
    }

    getUserTrainingDurations(): Observable<TrainingDurationViewModel[]> {
        return this.httpClient.get<TrainingDurationViewModel[]>(`${this.constants.apiURL()}/user/training/durations/`);
    }

    getUserTrainingActivities(): Observable<TrainingActivityViewModel[]> {
        return this.httpClient.get<TrainingActivityViewModel[]>(`${this.constants.apiURL()}/user/training/activities/`);
    }

    getUserTrainingTargets(): Observable<TrainingTargetViewModel[]> {
        return this.httpClient.get<TrainingTargetViewModel[]>(`${this.constants.apiURL()}/user/training/targets/`);
    }

    getUserActivities(): Observable<ActivityViewModel[]> {
        return this.httpClient.get<ActivityViewModel[]>(`${this.constants.apiURL()}/user/activities/`);
    }

    getGlobalProfiles(): Observable<GlobalProfilesViewModel[]> {
        return this.httpClient.get<GlobalProfilesViewModel[]>(`${this.constants.apiURL()}/global/profiles/`);
    }

    getFatHold(): Observable<FatHoldViewModel[]> {
        return this.httpClient.get<FatHoldViewModel[]>(`${this.constants.apiURL()}/fathold/`);
    }

    getHowLongFoodPrepare(): Observable<HowLongFoodPrepareViewModel[]> {
        return this.httpClient.get<HowLongFoodPrepareViewModel[]>(`${this.constants.apiURL()}/howlongfoodprepare/`);
    }

    getWhenPrepareFood(): Observable<WhenPrepareFoodViewModel[]> {
        return this.httpClient.get<WhenPrepareFoodViewModel[]>(`${this.constants.apiURL()}/whenpreparefood/`);
    }

    getUserGenders(): Observable<GenderViewModel[]> {
        return this.httpClient.get<GenderViewModel[]>(`${this.constants.apiURL()}/user/genders/`);
    }

    getUserBodyTypes(): Observable<BodyTypeViewModel[]> {
        return this.httpClient.get<BodyTypeViewModel[]>(`${this.constants.apiURL()}/user/body/types/`);
    }

    getUserDailyMeals(): Observable<DailyMealViewModel[]> {
        return this.httpClient.get<DailyMealViewModel[]>(`${this.constants.apiURL()}/user/daily/meals/`);
    }

    getUserDiets(): Observable<DietViewModel[]> {
        return this.httpClient.get<DietViewModel[]>(`${this.constants.apiURL()}/user/diets/`);
    }

    getUserCateringMeals(): Observable<CateringUserViewModel[]> {
        return this.httpClient.get<CateringUserViewModel[]>(`${this.constants.apiURL()}/user/catering/meals`);
    }

    updateUserProfile(userProfile: UserProfileViewModel): Observable<UserProfileViewModel> {
        return this.httpClient.put<UserProfileViewModel>(`${this.constants.apiURL()}/user/profile/`, userProfile)
            .pipe(
                // tslint:disable-next-line:no-shadowed-variable
                map(userProfile => {
                    this.authEventsEmitter.broadcastUserProfileChanged(userProfile);

                    return userProfile;
                })
            );
    }

    updateMasterUserProfile(user_id, userProfile: UserProfileViewModel): Observable<UserProfileViewModel> {
        return this.httpClient.put<UserProfileViewModel>(`${this.constants.apiURL()}/user/master/profile/`, {
            data: userProfile,
            user_id: user_id
        })
            .pipe(
                // tslint:disable-next-line:no-shadowed-variable
                map(userProfile => {
                    // this.authEventsEmitter.broadcastUserProfileChanged(userProfile);

                    return userProfile;
                })
            );
    }

    updateUserLang(language: Languages) {
        return this.httpClient.put<void>(`${this.constants.apiURL()}/user/profile/`, {locale: language});
    }

    getUserWater(date: string = formatDate(new Date(), this.constants.defaultAPIDateFormat)): Observable<UserWaterViewModel> {
        return this.httpClient.get<UserWaterViewModel>(`${this.constants.apiURL()}/user/water/${date}/`);
    }

    addUserWater(date: string = formatDate(new Date(), this.constants.defaultAPIDateFormat), data: AddMlToUserWaterDTO): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/user/water/${date}/`, data);
    }

    getReferrals(): Observable<ReferralViewModel[]> {
        return this.httpClient.get<ReferralViewModel[]>(`${this.constants.apiURL()}/user/referrals/`);
    }

    getReferralsStatistic(): Observable<ReferralsStatisticViewModel> {
        return this.httpClient.get<ReferralsStatisticViewModel>(`${this.constants.apiURL()}/user/referrals/statistic/`);
    }

    getIsCurrentUserReferral(): Observable<IsCurrentUserReferralViewModel> {
        return this.httpClient.get<IsCurrentUserReferralViewModel>(`${this.constants.apiURL()}/user/referral/check/`);
    }

    updateReferralName(referral_name: string): Observable<UpdatedReferralNameViewModel> {
        return this.httpClient.put<UpdatedReferralNameViewModel>(`${this.constants.apiURL()}/user/referrals/`, {
            referral_name: referral_name
        });
    }

    getTrainings(): Observable<TrainingViewModel[]> {
        return this.httpClient.get<TrainingViewModel[]>(`${this.constants.apiURL()}/user/trainings/`);
    }

    updateTrainings(data): Observable<void> {
        return this.httpClient.put<void>(`${this.constants.apiURL()}/user/trainings/`, data);
    }

    getMasterTrainings(user_id): Observable<TrainingViewModel[]> {
        return this.httpClient.post<TrainingViewModel[]>(`${this.constants.apiURL()}/user/master/trainings/`, {user_id: user_id});
    }

    updateMasterTrainings(data): Observable<void> {
        return this.httpClient.put<void>(`${this.constants.apiURL()}/user/master/trainings/`, data);
    }

    deleteUserAccount(): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/user/danger/delete-account/`, {});
    }

    sendVerificationEmail(): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/email/verify/`, {});
    }

    verifyEmail(code: string): Observable<void> {
        return this.httpClient.put<void>(`${this.constants.apiURL()}/email/verify/`, {
            'code': code
        });
    }

    getDailyChangesActivities(): Observable<DailyChangesActivitiesViewModel[]> {
        return this.httpClient.get<DailyChangesActivitiesViewModel[]>(`${this.constants.apiURL()}/user/daily/changes/activities/`);
    }

    getUserPreferredProducts(): Observable<UserPreferredProductViewModel[]> {
        return this.httpClient.get<UserPreferredProductViewModel[]>(`${this.constants.apiURL()}/user/preferred-products/`);
    }

    setUserPreferredProducts(ids: number[]): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/user/preferred-products/`, {
            'product_ids': ids
        });
    }

    getUserPreferredGlobalCategories(): Observable<UserPreferredGlobalCategoryViewModel[]> {
        return this.httpClient.get<UserPreferredGlobalCategoryViewModel[]>(`${this.constants.apiURL()}/user/preferred-global-categories/`);
    }

    setUserPreferredCategories(ids: number[]): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/user/preferred-global-categories/`, {
            'category_ids': ids
        });
    }

    adminLogin(token: string): Observable<AdminLoginUser> {
        return this.httpClient.post<AdminLoginUser>(`${this.constants.apiURL()}/admin/user/login/`, {
            'token': token
        });
    }

    getUserSport(): Observable<UserSport> {
        return this.httpClient.get<UserSport>(`${this.constants.apiURL()}/user/sport`);
    }
}
